import { useState, useEffect } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { IconButton, Grid, Alert, Snackbar, AlertColor } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link } from "react-router-dom";
import TollnerSepsis from "../create";
import { useTranslate } from "src/locales";
import { tollnerSepsisService, TollnerSepsisFormData } from "src/api/nurse/tollnerSepsisService";
import BaseMedCardList from "src/components/shared-components/medcard/BaseMedCardList";

function TollnerSepsisList() {
    const { t } = useTranslate();
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>("");
    const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");
    const [tollnerSepsisData, setTollnerSepsisData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    
    // Fetch data from service
    const fetchTollnerSepsisData = async () => {
        try {
            setIsLoading(true);
            const data = await tollnerSepsisService.listTollnerSepsis();
            setTollnerSepsisData(data);
        } catch (error) {
            console.error("Error fetching Töllner Sepsis data:", error);
            setAlertMessage("Veri yüklenirken hata oluştu!");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Load data when component mounts
    useEffect(() => {
        fetchTollnerSepsisData();
    }, []);
    
    const handleDelete = async (id: number): Promise<void> => {
        try {
            await tollnerSepsisService.deleteTollnerSepsis(id);
            // Update local state after successful deletion
            const newData = tollnerSepsisData.filter(row => row.id !== id);
            setTollnerSepsisData(newData);
            
            setAlertMessage("Kayıt başarıyla silindi");
            setAlertSeverity("success");
            setAlertOpen(true);
        } catch (error) {
            console.error("Error deleting record:", error);
            setAlertMessage("Kayıt silinirken hata oluştu!");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    };
    
    const columns: GridColDef[] = [
        {
            field: "update",
            headerName: "Güncelle",
            width: 80,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Link to={`edit/${params.row.id}`}>
                        <EditIcon color="info" />
                    </Link>
                );
            },
        },
        { field: "date", headerName: "Tarih", flex: 1 },
        { field: "skinColorChange", headerName: "Deri Rengi Değişikliği", flex: 1 },
        { field: "peripheralCirculation", headerName: "Periferik Dolanım", flex: 1 },
        { field: "hypotonia", headerName: "Hipotoni", flex: 1 },
        { field: "bradycardia", headerName: "Bradikardi", flex: 1 },
        { field: "apnea", headerName: "Apne", flex: 1 },
        { field: "respiratoryDistress", headerName: "Respiratuar Distres", flex: 1 },
        { field: "score", headerName: "Skor", flex: 1 },
        { field: "sepsisStatus", headerName: "Sepsis Durumu", flex: 1 },
        {
            field: "delete",
            headerName: "Sil",
            width: 60,
            renderCell: (params: GridRenderCellParams) => (
                <IconButton onClick={() => handleDelete(params.row.id as number)}>
                    <DeleteIcon color="error" />
                </IconButton>
            ),
        },
    ];
    
    const handlePrint = (): void => {
        try {
            console.log("Print functionality triggered");
            // Implementation for printing would go here
            
            setAlertMessage("Yazdırma işlemi başlatıldı");
            setAlertSeverity("info");
            setAlertOpen(true);
        } catch (error) {
            console.error("Error printing:", error);
            setAlertMessage("Yazdırma işlemi başlatılırken hata oluştu!");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    };
    
    // Handle form submission from the TollnerSepsis component
    const handleFormSubmission = async (formData: TollnerSepsisFormData | null): Promise<void> => {
        if (formData === null) {
            // This was a deletion
            // Data will be refreshed in the delete handler
            return;
        }
        
        try {
            // Save the new record using the service
            const savedRecord = await tollnerSepsisService.createTollnerSepsis(formData);
            
            // Add the new record to the local state
            setTollnerSepsisData([...tollnerSepsisData, {
                id: savedRecord.id,
                ...formData,
                date: formData.date ? new Date(formData.date).toLocaleString() : new Date().toLocaleString()
            }]);
            
            setAlertMessage("Yeni kayıt başarıyla eklendi");
            setAlertSeverity("success");
            setAlertOpen(true);
        } catch (error) {
            console.error("Error saving record:", error);
            setAlertMessage("Kayıt eklenirken hata oluştu!");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    };
    
    const handleCloseAlert = (): void => {
        setAlertOpen(false);
    };
    
    return (
        <>
            <Snackbar 
                open={alertOpen} 
                autoHideDuration={6000} 
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleCloseAlert} 
                    severity={alertSeverity} 
                    sx={{ width: '100%' }}
                >
                    {alertMessage}
                </Alert>
            </Snackbar>
        
            <BaseMedCardList
                title="Töllner Sepsis Formu"
                CreateComponent={({ onSave }: { onSave: (data: TollnerSepsisFormData | null) => Promise<void> }) => 
                    <TollnerSepsis onSave={onSave || handleFormSubmission} />
                }
                columns={columns}
                rows={tollnerSepsisData}
                onPrint={handlePrint}
            >
                {/* Score legend/explanation */}
                <Grid container spacing={1} style={{ width: "100%" }}>
                    <Grid
                        component="div"
                        item
                        xs={3}
                        sx={{
                            padding: "10px",
                            color: "black",
                        }}
                    >
                        <b>{t("tollner-sepsis.score-notes.notes")} </b>
                        <br />
                        {t("tollner-sepsis.score-notes.low-score")} 
                        <br />
                        {t("tollner-sepsis.score-notes.medium-score")} 
                        <br />
                        {t("tollner-sepsis.score-notes.high-score")} 
                    </Grid>
                </Grid>
            </BaseMedCardList>
        </>
    );
}

export default TollnerSepsisList;